import {
    DateStrategyAssign,
    OfferingType,
    PackageOfferingDto,
    CurrencyPrice,
    PriceBandRow,
    PricePeriods,
    StandardOfferingDto,
} from "app/shared/model/EditPricelist";
import { productTypeIdFromName } from "./productType";
import { normIso } from "../currency/normalizeCurrency";

const MAX_UNITS = 2147483647;

const buildPrices = (currencyIsoCodes: string[]): CurrencyPrice[] => {
    const unique = Array.from(
        new Set(currencyIsoCodes.map((iso) => normIso(iso)).filter(Boolean))
    );
    return unique.map((currencyIsoCode) => ({
        currencyIsoCode,
        pricePerMajorUnit: 0,
        pricePerMinorUnit: 0,
    }));
};

export const createDefaultPriceBand = (
    currencyIsoCodes: string[]
): PriceBandRow => ({
    minUnits: 1,
    maxUnits: MAX_UNITS,
    prices: buildPrices(currencyIsoCodes),
});

export const createDefaultPricePeriod = (args: {
    startDate: string;
    endDate: string;
    currencyIsoCodes: string[];
    priceBandStrategyId?: number;
}): PricePeriods => ({
    startDate: args.startDate as any,
    endDate: args.endDate as any,
    priceBandStrategyId: args.priceBandStrategyId ?? 1,
    priceBands: [createDefaultPriceBand(args.currencyIsoCodes)],
});

export type NewNonFeeOffering =
    | { ok: true; offering: StandardOfferingDto | PackageOfferingDto }
    | { ok: false; error: string };

export const createNewNonFeeOffering = (args: {
    productTypeName: string;
    productId: number;
    name: string;
    schoolIds: { id: number }[];
    currencyIsoCodes: string[];
    periodStartDate: string;
    periodEndDate: string;
    dateStrategyAssignments?: DateStrategyAssign[];
}): NewNonFeeOffering => {
    const productTypeId = productTypeIdFromName(args.productTypeName);
    if (!productTypeId) {
        return { ok: false, error: `Invalid product type: ${args.productTypeName}` };
    }

    const type = args.productTypeName as OfferingType;
    const offeringType = type === "Package" ? "package" : "standard";

    const defaultPricePeriod = createDefaultPricePeriod({
        startDate: args.periodStartDate,
        endDate: args.periodEndDate,
        currencyIsoCodes: args.currencyIsoCodes,
        priceBandStrategyId: 1,
    });

    const base = {
        offeringType,
        type,
        productId: {
            id: args.productId,
            type: { id: productTypeId, name: args.productTypeName },
        },
        name: args.name,
        schoolIds: args.schoolIds ?? [],
        pricePeriods: [defaultPricePeriod],
        dateStrategyAssignments: (args.dateStrategyAssignments ?? []).map((ds) => ({
            ...ds,
            isNew: true,
        })),
        isUpdated: false,
        isNew: true,
        productIdText: `${args.productTypeName}-${args.productId}`,
        hasPendingChanges: false,
    };

    if (type === "Package") {
        const offering: PackageOfferingDto = {
            ...(base as any),
            offeringType: "package",
            type: "Package",
            packageOfferingBreakdowns: [],
        };
        return { ok: true, offering };
    }

    return { ok: true, offering: base as StandardOfferingDto };
};
